import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import Seo from '../../components/Seo';
import AnimatedEyebrow from '../../components/AnimatedEyebrow';
import SparePartsFiltersSection from '../../components/spare-parts-page/SparePartsFiltersSection';
import SparePartsGridSection from '../../components/spare-parts-page/SparePartsGridSection';
import SparePartsSupportSection from '../../components/spare-parts-page/SparePartsSupportSection';
import Shortcuts from '../../components/services-page/Shortcuts';
import PageLoader from '../../components/PageLoader';
import { useI18n } from '../../context/I18nContext';
import { buildSparePartSearchText, filterAndRankByQuery } from '../../lib/search';
import { normalizeCategoryKey } from '../../lib/sparePartCategoryValidation';
import { localizePath } from '../../lib/localizedRoutes';
import { absoluteUrl, getSiteUrl } from '../../lib/seo';
import { getImageUrl, normalizeImageList } from '../../lib/imageData';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import useUrlSyncedFilters from '../../hooks/useUrlSyncedFilters';

const SPARE_PARTS_PER_PAGE = 6;
const DEFAULT_CATEGORY = 'all';
const ALL_CATEGORY_VALUE = 'all';
const SEARCH_DEBOUNCE_MS = 300;

async function fetchJsonOrThrow(url) {
  const response = await fetch(url, { credentials: 'include' });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error('Request failed');
  }
  return data;
}

export default function SparePartsPage() {
  const { language, t } = useI18n();
  const siteUrl = getSiteUrl();
  const allLabel = t('common.all', 'All');
  const title = language === 'fr' ? 'Catalogue pièces de rechange' : 'Spare parts catalog';
  const description =
    language === 'fr'
      ? 'Parcourez le catalogue de pièces de rechange ETC et trouvez rapidement les références disponibles.'
      : 'Browse the ETC spare parts catalog and quickly find available references.';
  const [spareParts, setSpareParts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // ---- URL-synced filter state ------------------------------------------------
  // `query` is the live (instant) text input value; `committedQuery` is the
  // debounced value used for filtering and for writing to the URL. Category
  // is committed immediately because clicks are discrete.
  const {
    values: { q: liveQuery, category, page: urlPage },
    setField,
    isReady: filtersReady,
  } = useUrlSyncedFilters({
    fields: {
      q: { defaultValue: '' },
      category: { defaultValue: DEFAULT_CATEGORY },
      page: {
        defaultValue: '1',
        parse: (raw) => {
          const n = parseInt(raw, 10);
          return Number.isFinite(n) && n > 0 ? String(n) : '1';
        },
        serialize: (value) => String(Math.max(1, parseInt(value, 10) || 1)),
      },
    },
  });

  const committedQuery = useDebouncedValue(liveQuery, SEARCH_DEBOUNCE_MS);

  // While the debounce timer is pending, the URL still holds the previous
  // committed query. Sync it to the debounced value when the timer fires.
  useEffect(() => {
    if (!filtersReady) return;
    if (committedQuery !== liveQuery) {
      setField('q', committedQuery);
    }
    // We intentionally exclude `liveQuery` so this only runs when the
    // debounced value settles, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [committedQuery, filtersReady]);

  // ---- Data fetching ----------------------------------------------------------
  useEffect(() => {
    let isMounted = true;

    fetchJsonOrThrow(`/api/spare-parts?lang=${language}`)
      .then((data) => {
        if (isMounted) {
          setSpareParts(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {
        if (!isMounted) return;
        setSpareParts([]);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [language]);

  useEffect(() => {
    let isMounted = true;

    fetchJsonOrThrow(`/api/spare-part-categories?lang=${language}`)
      .then((data) => {
        if (!isMounted) return;
        setCategories(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!isMounted) return;
        setCategories([]);
      });

    return () => {
      isMounted = false;
    };
  }, [language]);

  // ---- Derived options and filtered list --------------------------------------
  const categoryOptions = useMemo(
    () => {
      // API already returns deduplicated, formatted options - just add 'all' option
      const raw = [
        { value: 'all', label: allLabel },
        ...(Array.isArray(categories) ? categories : []),
      ];
      // Dedupe by normalized value to ensure no duplicates
      const deduped = Array.from(
        raw.reduce((acc, option) => {
          const key = normalizeCategoryKey(String(option?.value || '').trim()) || String(option?.label || '').trim().toLowerCase();
          if (!key) return acc;
          if (!acc.has(key)) acc.set(key, option);
          return acc;
        }, new Map()).values(),
      );
      return deduped;
    },
    [allLabel, categories],
  );

  const filteredParts = useMemo(() => {
    // Use the committed (debounced) query so heavy search ranking only runs
    // after the user has paused typing. The category click is instant.
    const searchMatched = filterAndRankByQuery(spareParts, committedQuery, buildSparePartSearchText);
    return searchMatched.filter((item) => {
      if (category === 'all') return true;
      const normalizedSelectedCategory = normalizeCategoryKey(category);
      const itemCategoryKey = String(item?.categoryKey || '').trim();
      const itemCategory = String(item?.category || '').trim();
      const itemCategoryEn = String(item?.categoryI18n?.en || '').trim();
      const itemCategoryFr = String(item?.categoryI18n?.fr || '').trim();
      const normalizedItemCategories = [itemCategoryKey, itemCategory, itemCategoryEn, itemCategoryFr]
        .filter(Boolean)
        .map((value) => normalizeCategoryKey(value));
      if (
        itemCategoryKey !== category &&
        itemCategory !== category &&
        itemCategoryEn !== category &&
        itemCategoryFr !== category &&
        !normalizedItemCategories.includes(normalizedSelectedCategory)
      ) {
        return false;
      }
      return true;
    });
  }, [category, committedQuery, spareParts]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredParts.length / SPARE_PARTS_PER_PAGE)),
    [filteredParts.length],
  );

  // Pagination state is URL-driven so deep links to a specific page work.
  const parsedPage = Math.max(1, parseInt(urlPage, 10) || 1);
  const currentPage = Math.min(parsedPage, totalPages);

  // Whenever a filter changes such that the current page no longer exists,
  // snap it back to page 1 in the URL.
  useEffect(() => {
    if (!filtersReady) return;
    if (currentPage !== parsedPage) {
      setField('page', String(currentPage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, parsedPage, filtersReady]);

  const paginatedParts = useMemo(() => {
    const startIndex = (currentPage - 1) * SPARE_PARTS_PER_PAGE;
    return filteredParts.slice(startIndex, startIndex + SPARE_PARTS_PER_PAGE);
  }, [currentPage, filteredParts]);

  const visibleStart = filteredParts.length === 0 ? 0 : (currentPage - 1) * SPARE_PARTS_PER_PAGE + 1;
  const visibleEnd = filteredParts.length === 0 ? 0 : visibleStart + paginatedParts.length - 1;
  const sparePartItemListJsonLd = paginatedParts.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: title,
        numberOfItems: filteredParts.length,
        itemListElement: paginatedParts.map((item, index) => {
          const sparePartId = String(item?._id || item?.id || '').trim();
          const firstImage = normalizeImageList(item?.images)[0];
          const imageUrl = firstImage ? absoluteUrl(getImageUrl(firstImage), siteUrl) : undefined;

          return {
            '@type': 'ListItem',
            position: visibleStart + index,
            name: item?.name || sparePartId,
            url: absoluteUrl(localizePath(`/spare-parts/${sparePartId}`, language), siteUrl),
            image: imageUrl,
          };
        }),
      }
    : null;
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: language === 'fr' ? 'Accueil' : 'Home',
        item: absoluteUrl('/', siteUrl),
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: title,
        item: absoluteUrl('/spare-parts', siteUrl),
      },
    ],
  };
  const introTitle = language === 'fr'
    ? 'Trouver la bonne pièce'
    : 'Find the right spare part';
  const introTitleLine1 = language === 'fr' ? 'TROUVEZ VOTRE PROCHAINE' : 'FIND YOUR NEXT';
  const introTitleLine2 = language === 'fr' ? 'PIÈCE DE RECHANGE' : 'SPARE PART';
  const introText = language === 'fr'
    ? 'Parcourez les familles de pièces, utilisez la recherche rapide et gardez un oeil sur les références compatibles pour accélérer votre demande.'
    : 'Browse spare part families, use quick search, and keep an eye on compatible references to speed up your request.';

  // ---- Render -----------------------------------------------------------------
  if (isLoading) {
    return (
      <>
        <Seo
          title={title}
          description={description}
          jsonLd={[breadcrumbJsonLd, sparePartItemListJsonLd].filter(Boolean)}
          keywords={{
            en: ['spare parts catalog', 'forklift spare parts', 'replacement parts', 'warehouse equipment parts', 'industrial components'],
            fr: ['catalogue pièces de rechange', 'pièces de rechange chariots élévateurs', 'pièces de remplacement', 'composants industriels', 'matériel de manutention'],
          }}
        />
        <PageLoader label={t('sparePartsPage.loading', 'Loading spare parts...')} />
      </>
    );
  }

  return (
    <section className="relative min-h-screen overflow-x-hidden bg-[#0a0a0c] text-white selection:bg-[var(--dash-main)] selection:text-black">
      <Seo
        title={title}
        description={description}
        jsonLd={[breadcrumbJsonLd, sparePartItemListJsonLd].filter(Boolean)}
        keywords={{
          en: ['spare parts catalog', 'forklift spare parts', 'replacement parts', 'warehouse equipment parts', 'industrial components'],
          fr: ['catalogue pièces de rechange', 'pièces de rechange chariots élévateurs', 'pièces de remplacement', 'composants industriels', 'matériel de manutention'],
        }}
      />
      <div className="pointer-events-none absolute inset-0 z-0">
        <div className="absolute left-0 top-0 h-full w-full bg-[radial-gradient(circle_at_15%_25%,rgba(255,93,16,0.08)_0%,transparent_40%)]" />
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]" />
      </div>
      <main className="container relative z-10 mx-auto px-6 pb-20 lg:px-10">
      <section className="pt-0 pb-14 lg:pt-0 lg:pb-20">
        <div className="mx-auto max-w-5xl text-center text-white">
          <AnimatedEyebrow className="mx-auto mb-6 w-fit">
            {language === 'fr' ? 'Pièces de rechange' : 'Spare parts'}
          </AnimatedEyebrow>
          <h2 className="mx-auto mt-6 max-w-4xl text-4xl font-light uppercase leading-[0.94] tracking-[-0.04em] sm:text-5xl lg:text-6xl">
            <span className="block text-white">{introTitleLine1}</span>
            <span className="mt-2 block font-black bg-gradient-to-r from-orange-400 via-[var(--dash-main)] to-orange-700 bg-clip-text text-transparent">
              {introTitleLine2}
            </span>
          </h2>
          <p className="mx-auto mt-5 max-w-4xl text-sm font-light leading-relaxed text-slate-300 md:text-base">
            {introText}
          </p>
        </div>
      </section>
      <SparePartsFiltersSection
        query={liveQuery}
        onQueryChange={(value) => setField('q', value)}
        category={category || ALL_CATEGORY_VALUE}
        onCategoryChange={(value) => setField('category', value || ALL_CATEGORY_VALUE)}
        categories={categoryOptions}
        visibleStart={visibleStart}
        visibleEnd={visibleEnd}
        totalCount={filteredParts.length}
      />
      <SparePartsGridSection
        spareParts={paginatedParts}
        isLoading={false}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={(next) => setField('page', String(next))}
      />
      <SparePartsSupportSection />
      </main>
      <Shortcuts />
    </section>
  );
}
